import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import LoadingBarApp from "../../widgets/loading";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase/firebase";
import { Solution } from "../../classes/solution.tsx";
import { useTranslation } from "react-i18next";
import { FormControl, IconButton, InputLabel, MenuItem, Select, ThemeProvider, Tooltip } from "@mui/material";
import theme from "../../style/theme.jsx";
import { AccountCircleOutlined, ArrowBackOutlined, ArrowDownwardOutlined, ArrowForwardOutlined, ArrowUpwardOutlined, CopyAllOutlined, DeleteOutlined, Error, SaveOutlined, SettingsOutlined } from "@mui/icons-material";
import { EditableText, MultilingualEditableText } from "../../widgets/textFields.jsx";
import MessageDialog from "../../widgets/popups/messageDialog.jsx";
import ImageEditable, { ChangeDeleteImage } from "../../widgets/edit/imageEditable.jsx";
import TopEditMenu, { EditMenuSeparator } from "../../widgets/topEditMenu.jsx";
import LanguageSwitcher from "../../widgets/language.jsx";
import { MaterialIcon } from "../../classes/materialIcon.jsx";
import { hasPermission } from "../../firebase/firestore/permisions.jsx";
import { SolutionPricingCard } from "../../widgets/solutions/cards.jsx";
import { FeatureDialog, NewFeatureDialog, PriceDialog, SettingsDialog } from "../../widgets/solutions/dialogs.jsx";
import { AvatarContextMenu } from "../../classes/utils/contextMenus.tsx";
import { Position } from "../../classes/utils/position.tsx";
import { AvatarContextMenuJSX } from "../../widgets/contextMenus.jsx";

const languages = ['ca', 'en', 'es'];

export default function SolutionPage() {
    const { t, i18n } = useTranslation();

    const docId = useParams().id;

    const [canEdit, setCanEdit] = useState(null);

    const [solution, setSolution] = useState(null);
    const [updateValue, setUpdateValue] = useState(false);
    const [unsavedChanges, setUnsavedChanges] = useState(false);

    const [editing, setEditing] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const [openSettings, setOpenSettings] = useState(false);

    const [language, setLanguage] = useState(i18n.resolvedLanguage??'en');

    const [editingFeature, setEditingFeature] = useState(null);

    const [newFeature, setNewFeature] = useState(false);
    const [featureIdError, setFeatureIdError] = useState(false);

    const [editingPrice, setEditingPrice] = useState(null);

    const [avatarMenu, setAvatarMenu] = useState(null);

    async function getCanEdit() {
        const response = await hasPermission('solution-writer');
        setCanEdit(response);
    }

    useEffect(() => {
        const handler = (event) => {
            event.preventDefault();
            event.returnValue = "";
        };

        if (unsavedChanges) {
            window.addEventListener("beforeunload", handler);
            return () => {
                window.removeEventListener("beforeunload", handler);
            };
        }
        return () => {};
    }, [unsavedChanges]);

    function update() {
        setUpdateValue(!updateValue);
        if (!unsavedChanges) {
            setUnsavedChanges(true);
        }
    }

    async function getSolution() {
        const data = await getDoc(doc(db, 'solutions', 'solutions', 'solutions', docId));

        if (data.exists()) {
            setSolution(Solution.fromJSON(data.data(), docId));
        } else {
            setSolution(undefined);
        }
    }

    if (solution === null) {
        getSolution();

        return <div style={{minHeight: window.innerHeight}}>
            <LoadingBarApp/>
        </div>;
    }

    if (solution === undefined) {
        return <div>
            <div>
                <div className="solution-not-found-page">
                    <section className="solution-error-content">
                        <div className="solution-error-container">
                            <div className="solution-error-illustration">
                                <ThemeProvider theme={theme}>
                                    <Error color="secondary" sx={{fontSize: '160px'}}/>
                                </ThemeProvider>
                            </div>
                            <h1>{t('solutions.notFound.title')}</h1>
                            <p className="solution-error-message">{t('solutions.notFound.subtitle')}</p>
                            
                            <div className="solution-suggestions">
                                <h3>{t('solutions.notFound.options')}</h3>
                                <ul>
                                    <li><a href="/solutions">{t('solutions.notFound.products')}</a></li>
                                    <li><a href="/contact">{t('solutions.notFound.contact')}</a></li>
                                </ul>
                            </div>
                            <a href="/" className="solution-home-button">{t('solutions.notFound.button')}</a>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    }

    if (canEdit === null) {
        getCanEdit();
    }

    function edit() {
        if (!editing) {
            setEditing(true);
            return true;
        } else {
            return false;
        }
    }

    function stopEditing() {
        setEditing(false);
    }

    const handleClosePopup = () => {
        setShowPopup(false);
    }
    const handleShowPopup = () => {
        setShowPopup(true);
    }

    const handleOnStartEditing = () => {
        const canEdit = edit();

        if (!canEdit) {
            handleShowPopup();
        }

        return canEdit;
    }
    const handleOnEndEditing = () => {
        stopEditing();
    }

    const handleCloseFeature = () => {
        if (!editing) {
            setEditingFeature(null);
        } else {
            handleShowPopup();
        }
    }
    const handleOpenFeature = (feature) => {
        if (!editing) {
            setEditingFeature(feature);
        } else {
            handleShowPopup();
        }
    }

    const handleCloseNewFeature = () => {
        setNewFeature(false);
    }
    const handleOpenNewFeature = () => {
        setNewFeature(true);
        setFeatureIdError(false);
    }

    const handleCloseEditingPrice = () => {
        if (!editing) {
            setEditingPrice(null);
        } else {
            handleShowPopup();
        }
    }
    const handleOpenEditingPrice = (price) => {
        if (!editing) {
            setEditingPrice(price);
        } else {
            handleShowPopup();
        }
    }

    const handleAvatarContextMenu = (event, avatar) => {
        if (canEdit) {
            event.preventDefault();

            setAvatarMenu(new AvatarContextMenu(new Position(event.clientX+2, event.clientY-6), avatar));

            const selection = document.getSelection();
            if (selection && selection.rangeCount > 0) {
                const range = selection.getRangeAt(0);

                setTimeout(() => {
                    selection.addRange(range);
                });
            }
        }
    }
    const handleAvatarContextMenuClose = () => {
        setAvatarMenu(null);
    }

    const handleOpenSettings = () => {
        setOpenSettings(true);
    }
    const handleCloseSettings = () => {
        setOpenSettings(false);
    }

    return <div>
        <AvatarContextMenuJSX
            open={avatarMenu !== null}
            onClose={handleAvatarContextMenuClose}
            position={avatarMenu !== null ? avatarMenu.getPosition() : null}
            onChange={(url)=>{
                avatarMenu.getAvatar().changePhoto(url);
                update();
                handleAvatarContextMenuClose();
            }}
            onDelete={()=>{
                if (avatarMenu.getAvatar().getPhoto() !== 'user') {
                    avatarMenu.getAvatar().deletePhoto();
                    update();
                }
                handleAvatarContextMenuClose();
            }}
        />
        <SettingsDialog open={openSettings} onClose={handleCloseSettings} onUpdate={()=>{update();}} solution={solution} onStartEditing={handleOnStartEditing} onEndEditing={handleOnEndEditing} canEdit={canEdit}/>
        <MessageDialog
            open={showPopup}
            onClose={handleClosePopup}
            title={t('solutions.page.warning.title')}
            message={t('solutions.page.warning.message')}
        />
        <TopEditMenu>
            <LanguageSwitcher onChange={(lang)=>{
                setLanguage(lang);
            }}/>
            <EditMenuSeparator/>
            <ThemeProvider theme={theme}><IconButton sx={{fontSize: '15px', padding: 0}}><SettingsOutlined fontSize="10px" color="primary" className="clickable" onClick={()=>{
                handleOpenSettings();
            }}></SettingsOutlined></IconButton></ThemeProvider>
            {unsavedChanges
                ? <>
                    <EditMenuSeparator/>
                    <ThemeProvider theme={theme}>
                        <SaveOutlined color="primary" fontSize="10px" className="clickable" onClick={() => {
                            console.log('Save');
                        }}/>
                    </ThemeProvider>
                </>
                : null
            }
        </TopEditMenu>
        <FeatureDialog
            open={editingFeature !== null}
            onClose={handleCloseFeature}
            editingFeature={editingFeature}
            onUpdate={() => {
                update();
            }}
            canEdit={canEdit}
            language={language}
            onStartEditing={handleOnStartEditing}
            onEndEditing={handleOnEndEditing}
        />
        <NewFeatureDialog open={newFeature} onClose={handleCloseNewFeature} solution={solution} onUpdate={()=>{
            update();
            handleOpenFeature(solution.getFeatures()[solution.getFeatures().length-1]);
        }} featureIdError={featureIdError} setFeatureIdError={setFeatureIdError}/>
        <PriceDialog open={editingPrice !== null} onClose={handleCloseEditingPrice} price={editingPrice} language={language} canEdit={canEdit} onUpdate={()=>{
            update();
        }} onEdit={handleOnStartEditing} onEndEditing={handleOnEndEditing} languages={languages} isEditing={editing} showPopUp={handleShowPopup}/>
        <div className="solution-page">
            <section className="solution-hero">
                <div className="solution-hero-content">
                    <div className="solution-app-info">
                        <div className="solution-app-icon">
                            <ImageEditable id='solution-logo' alt={solution.getName()} src={solution.getLogo()} onChange={(url) => {
                                solution.changeLogo(url);
                                update();
                            }} canEdit={canEdit}/>
                        </div>
                        <h1><EditableText id='title' text={solution.getName()} onSave={(value) => {
                            solution.setName(value);
                            update();
                        }} onStartEditing={handleOnStartEditing} onEnd={handleOnEndEditing} canEdit={canEdit}/></h1>
                        <MultilingualEditableText id='short-description' texts={solution.getShortDescription()} languages={languages} onSave={(value, language) => {
                            solution.setShortDescription(value);
                            update();
                        }} onStartEditing={handleOnStartEditing} onEnd={handleOnEndEditing} displayLanguage={language} tag="p" className="solution-tagline" canEdit={canEdit}/>
                        {solution.getDowloadMobileLinks()}
                        {solution.getWebsiteLink(t)}
                    </div>
                    <div className="solution-phone-mockup">
                        <ImageEditable id='solution-presentation-image' alt='Solution presentation image' src={solution.getPresentationImg()} onChange={(url) => {
                            solution.changePresentationImg(url);
                            update();
                        }} canEdit={canEdit}/>
                    </div>
                </div>
            </section>

            <section className="solution-features-section">
                <div className="solution-container">
                    <h2>{t('solutions.page.features')}</h2>
                    <ThemeProvider theme={theme}>
                        <div className="solution-features-grid">
                            {solution.getFeatures().map((feature, index) => <div className="solution-feature-card" key={index} onClick={() => {
                                if (canEdit) handleOpenFeature(feature);
                                else if (feature.getId() !== undefined && feature.getId() !== null && feature.getId().length > 0)
                                    document.querySelector(`#${feature.getId()}`).scrollIntoView({block: 'start', behavior: 'smooth'});
                            }} style={{position: 'relative'}}>
                                    <div className="solution-feature-icon">
                                        {feature.getMaterialIcon()}
                                    </div>
                                    <h3>{feature.getTitleByLang(language)}</h3>
                                    <p>{feature.getDescriptionByLang(language)}</p>
                                    {canEdit
                                        ? <div style={{
                                            position: 'absolute',
                                            top: 0,
                                            right: 0,
                                            display: 'flex',
                                            flexFlow: 'row',
                                            flexWrap: 'nowrap',
                                        }}>
                                            <Tooltip title={t('actions.moveLeft')}><IconButton onClick={index !== 0 ? (e) => {
                                                e.stopPropagation();
                                                solution.moveFeatureLeft(index);
                                                update();
                                            } : null} disabled={index === 0}><ArrowBackOutlined/></IconButton></Tooltip>
                                            <Tooltip title={t('actions.moveRight')}><IconButton onClick={index !== solution.getFeatures().length-1 ? (e) => {
                                                e.stopPropagation();
                                                solution.moveFeatureRight(index);
                                                update();
                                            } : null} disabled={index === solution.getFeatures().length-1}><ArrowForwardOutlined/></IconButton></Tooltip>
                                            <Tooltip title={t('actions.deleteNormal')}><IconButton onClick={(e) => {
                                                e.stopPropagation();
                                                solution.deleteFeature(index);
                                                update();
                                            }}><DeleteOutlined/></IconButton></Tooltip>
                                        </div> : null
                                    }
                                </div>)}

                            {canEdit
                                ? <div className="solution-feature-card" onClick={handleOpenNewFeature}>
                                    <div className="solution-feature-icon">
                                        <MaterialIcon icon={'add'}/>
                                    </div>
                                    <h3>{t('solutions.page.feature.new')}</h3>
                                    <p></p>
                                </div> : null
                            }
                        </div>
                    </ThemeProvider>
                </div>
            </section>

            <section className="solution-screenshots-section">
                <div className="solution-container">
                    <h2>{t('solutions.page.screenshot')}</h2>
                    <div className="solution-screenshots-carousel">
                        {solution.getImages().map((image, index) => <div className="solution-screenshot" key={index}>
                            <ChangeDeleteImage id={`image-${index}`} canEdit={canEdit} src={image} alt={`Screenshot ${index}`} onDelete={() => {
                                solution.deleteImage(index);
                                update();
                            }} onChange={(url)=>{
                                solution.changeImage(url, index);
                                update();
                            }} canMoveLeft={index !== 0} canMoveRight={index !== solution.getImages().length-1} onMoveLeft={()=>{
                                solution.moveImageLeft(index);
                                update();
                            }} onMoveRight={()=>{
                                solution.moveImageRight(index);
                                update();
                            }}/>
                        </div>)}
                        {canEdit
                            ? <div className="solution-feature-card" style={{minWidth: 150}} onClick={()=>{
                                document.getElementById('input-picker-new-image').click();
                            }}>
                                <div className="solution-feature-icon">
                                    <MaterialIcon icon={'add'}/>
                                </div>
                                <h3>{t('solutions.page.images.add')}</h3>
                                <input 
                                    id='input-picker-new-image'
                                    type="file"
                                    style={{display:'none'}}
                                    accept="image/webp"
                                    onChange={(e) => {
                                        solution.addImage(URL.createObjectURL(e.target.files[0]));
                                        update();
                                    }}
                                />
                            </div> : null
                        }
                    </div>
                </div>
            </section>

            <section className="solution-pricing-section">
                <div className="solution-container">
                    <h2>{t('solutions.page.pricing.title')}</h2>
                    {solution.getPricing().map((pricing, index) => <div key={index} className="solution-pricing-cards" style={{marginBottom: 40, position: 'relative'}}>
                        {pricing.map((price, indexi) => <SolutionPricingCard canEdit={canEdit} price={price} key={indexi} language={language} onDelete={(e)=>{
                            e.stopPropagation();
                            solution.deletePrice(index, indexi);
                            update();
                        }} onDuplicate={(e)=>{
                            e.stopPropagation();
                            solution.duplicatePrice(index, indexi);
                            update();
                        }} onClick={handleOpenEditingPrice} onMoveLeft={(e)=>{
                            e.stopPropagation();
                            solution.movePriceLeft(index, indexi);
                            update();
                        }} onMoveRight={(e)=>{
                            e.stopPropagation();
                            solution.movePriceRight(index, indexi);
                            update();
                        }} moveLeft={indexi !== 0} moveRight={indexi !== pricing.length-1}/>)}
                        {canEdit
                            ? <div className="solution-feature-card" style={{minWidth: 200, maxWidth: 200}} onClick={()=>{
                                solution.addPrice(index);
                                update();
                            }}>
                                <div className="solution-feature-icon">
                                    <MaterialIcon icon={'add'}/>
                                </div>
                                <h3>{t('solutions.page.pricing.add')}</h3>
                            </div> : null
                        }
                        {canEdit
                            ? <div style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                overflow: 'visible'
                            }}>
                                <Tooltip title={t('solutions.page.pricing.actions.duplicateSection')}><IconButton onClick={(e)=>{
                                    e.stopPropagation();
                                    solution.duplicateSectionPrice(index);
                                    update();
                                }}><CopyAllOutlined/></IconButton></Tooltip>
                                <Tooltip title={t('solutions.page.pricing.actions.deleteSection')}><IconButton onClick={(e)=>{
                                    e.stopPropagation();
                                    solution.deleteSectionPrice(index);
                                    update();
                                }}><DeleteOutlined/></IconButton></Tooltip>
                                <Tooltip title={t('actions.moveUp')}><IconButton onClick={index !== 0 ? ()=>{
                                    solution.moveFeatureSectionUp(index);
                                    update();
                                } : null} disabled={index === 0}><ArrowUpwardOutlined/></IconButton></Tooltip>
                                <Tooltip title={t('actions.moveDown')}><IconButton onClick={index !== solution.getPricing().length-1 ? ()=>{
                                    solution.moveFeatureSectionDown(index);
                                    update();
                                } : null} disabled={index === solution.getPricing().length-1}><ArrowDownwardOutlined/></IconButton></Tooltip>
                            </div> : null
                        }
                    </div>)}
                    {canEdit
                        ? <div className="solution-feature-card" style={{width: 'calc(100% - 60px)'}} onClick={()=>{
                            solution.addSectionPrice();
                            update();
                        }}>
                            <div className="solution-feature-icon">
                                <MaterialIcon icon={'add'}/>
                            </div>
                            <h3>{t('solutions.page.pricing.addSection')}</h3>
                        </div> : null
                    }
                </div>
            </section>

            <section>
                {solution.getLongFeatures().map((feature, index) => {
                    return <>
                        <MultilingualEditableText key={index} id={feature.id} texts={solution.getLongFeatures()[index].text} languages={languages} onSave={(value) => {
                            feature.text = value;
                            update();
                        }} onStartEditing={handleOnStartEditing} onEnd={handleOnEndEditing} displayLanguage={language} tag="div" className={`solution-long-feature ${index%2 === 0 ? '' : 'other-color'}`} canEdit={canEdit} sx={{width: 'unset'}} bottomWidget={<FormControl sx={{marginTop: '5px', maxWidth: '200px', width: '100%'}}>
                            <InputLabel id="editing-long-feature-id-label">{t('solutions.page.feature.id')}</InputLabel>
                            <Select
                                labelId="editing-long-feature-id-label"
                                id="editing-long-feature-id"
                                value={feature.id}
                                label={t('solutions.page.feature.id')}
                                onChange={(event)=>{
                                    feature.id = event.target.value;
                                    update();
                                }}
                            >
                                <MenuItem id="no-selection" value={''}>{t('solutions.page.feature.noId')}</MenuItem>
                                {solution.getAllFeaturesId().map((id, index)=>{
                                    return <MenuItem value={id} key={index}>{id}</MenuItem>;
                                })}
                            </Select>
                        </FormControl>}/>
                    </>
                })}
                {canEdit
                    ? <div className="solution-feature-card" style={{width: 'calc(100% - 60px)'}} onClick={()=>{
                        solution.createLargeFeature();
                        update();
                    }}>
                        <div className="solution-feature-icon">
                            <MaterialIcon icon={'add'}/>
                        </div>
                        <h3>{t('solutions.page.feature.addLarge')}</h3>
                    </div> : null
                }
            </section>

            <section className="solution-testimonials-section">
                <div className="solution-container">
                    <h2>{t('solutions.page.ourUsers')}</h2>
                    <div className="solution-testimonials-grid">
                        {solution.getUsers().map((user, index) => <div className="solution-testimonial-card" key={index} style={{position: 'relative'}}>
                            <MultilingualEditableText id={`user-text-${index}`} texts={user.getText()} languages={languages} onSave={(value) => {
                                user.setText(value);
                                update();
                            }} onStartEditing={handleOnStartEditing} onEnd={handleOnEndEditing} displayLanguage={language} tag="p" className="solution-quote" canEdit={canEdit}/>
                            <div className="solution-user">
                                <div className={canEdit?'clickable context-menu':''} onContextMenu={(event)=>{
                                    handleAvatarContextMenu(event, user);
                                }}>
                                    {user.getPhoto() !== undefined && user.getPhoto() !== 'user'
                                        ? <img src={user.getPhoto()} alt={user.getName()} width={50} height={50}/>
                                        : <AccountCircleOutlined/>
                                    }
                                </div>
                                <div className="solution-user-info" style={{
                                    width: 'calc(100% - 50px - 15px)'
                                }}>
                                    <div className="solution-name"><EditableText id='title' text={user.getName()} onSave={(value) => {
                                        user.setName(value);
                                        update();
                                    }} onStartEditing={handleOnStartEditing} onEnd={handleOnEndEditing} canEdit={canEdit}/></div>
                                    <MultilingualEditableText id={`user-description-${index}`} texts={user.getDescription()} languages={languages} onSave={(value) => {
                                        user.setDescription(value);
                                        update();
                                    }} onStartEditing={handleOnStartEditing} onEnd={handleOnEndEditing} displayLanguage={language} tag="div" className="solution-title" canEdit={canEdit}/>
                                </div>
                            </div>
                            {canEdit
                                ? <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    right: 0,

                                    display: 'flex',
                                    flexFlow: 'row',
                                    flexWrap: 'nowrap',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '5px',
                                }}>
                                    <Tooltip title={t('actions.moveLeft')}><IconButton onClick={index !== 0 ? ()=>{
                                        solution.moveUserLeft(index);
                                        update();
                                    } : null} disabled={index === 0}><ArrowBackOutlined/></IconButton></Tooltip>
                                    <Tooltip title={t('actions.moveRight')}><IconButton onClick={index !== solution.getUsers().length - 1 ? ()=>{
                                        solution.moveUserRight(index);
                                        update();
                                    } : null} disabled={index === solution.getUsers().length - 1}><ArrowForwardOutlined/></IconButton></Tooltip>
                                    <Tooltip title={t('actions.deleteNormal')}><IconButton onClick={()=>{
                                        solution.deleteUser(index);
                                        update();
                                    }}><DeleteOutlined/></IconButton></Tooltip>
                                </div> : null
                            }
                        </div>)}
                        {canEdit
                            ? <div className="solution-feature-card" style={{width: 'calc(100% - 60px)'}} onClick={()=>{
                                solution.addUser();
                                update();
                            }}>
                                <div className="solution-feature-icon">
                                    <MaterialIcon icon={'add'}/>
                                </div>
                                <h3>{t('actions.addUser')}</h3>
                            </div> : null
                        }
                    </div>
                </div>
            </section>

            <section className="solution-final-cta">
                <div className="solution-container">
                    <MultilingualEditableText id={`slogang-label`} texts={solution.getSlogang()} languages={languages} onSave={(value) => {
                        solution.getSlogang(value);
                        update();
                    }} onStartEditing={handleOnStartEditing} onEnd={handleOnEndEditing} displayLanguage={language} tag="h2" className="" canEdit={canEdit}/>
                    <div className="solution-download-buttons">
                        {solution.getDowloadMobileLinks()}
                    </div>
                    {solution.getWebsiteLink(t)}
                </div>
            </section>
        </div>
    </div>;
}